import React from 'react';
import { ProductStatusContext, ProductStatusContextProps } from '../context/ProductStatusContext';
import { ProductStatus } from '../types/order/productStatus';
import { useAppDispatch, useAppSelector } from '../ui/pages/store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 } from 'uuid';

const productStatusState = {
  items: new Map<string, ProductStatus>(),
  loading: false,
};

interface ProductStatusState {
  key: string;
}

export const productStatusSlice = createSlice({
  name: 'productStatus',
  initialState: { key: v4() } as ProductStatusState,
  reducers: {
    reset: state => {
      productStatusState.items = new Map();
      productStatusState.loading = true;
      state.key = v4();
    },
    set: (state, action: PayloadAction<ProductStatus[]>) => {
      productStatusState.items = new Map();
      action.payload.forEach(ps => productStatusState.items.set(ps.id, new ProductStatus(ps)));
      state.key = v4();
    },
    add: (state, action: PayloadAction<ProductStatus[]>) => {
      action.payload.forEach(ps => productStatusState.items.set(ps.id, new ProductStatus(ps)));
      productStatusState.items = new Map(productStatusState.items);
      state.key = v4();
    },
    delete: (state, action: PayloadAction<ProductStatus[]>) => {
      action.payload.forEach(ps => productStatusState.items.delete(ps.id));
      productStatusState.items = new Map(productStatusState.items);
      state.key = v4();
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      productStatusState.loading = action.payload;
      state.key = v4();
    },
  },
});

interface ProductStatusProviderProps {
  children: React.ReactNode;
}

export default function ProductStatusProvider({ children }: ProductStatusProviderProps) {
  const dispatch = useAppDispatch();
  const key = useAppSelector(state => state.productStatuses.key);

  const productStatusContext: ProductStatusContextProps = {
    productStatuses: productStatusState.items,
    productStatusesLoading: productStatusState.loading,
    setProductStatuses: (productStatuses: Map<string, ProductStatus>) => {
      dispatch(productStatusSlice.actions.set([...productStatuses.values()]));
    },
  };

  return <ProductStatusContext.Provider value={productStatusContext}>{children}</ProductStatusContext.Provider>;
}
